import React, {useEffect, useState} from "react";
import {t} from "../../utilities/TranslationUtils";
import Dialog from "@mui/material/Dialog";
import {AppCard, AppConfirmDialog, AppLightTooltip, AppSpacer, AppUseIcon} from "../../components/ui";
import {AppButton, AppInput} from "../../components/form";
import InputAdornment from "@mui/material/InputAdornment";
import ClientInfoDisplay from "./Components/ClientInfoDisplay";
import CopyClientInfoButton from "./Components/CopyClientInfoButton";

const ClientsAddDialog = (props: {
  isLoading: boolean;
  isAddingClient: boolean;
  isClientAdded: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isDialogOpened: boolean;
  secret: string;
  idPrefix: string;
}) => {
  const [id, setId] = useState("")
  const [name, setName] = useState("")
  const [isConfirmCopyOpen, setIsConfirmCopyOpen] = useState(false)
  const onIdChange = (idInput: string): void => {
    setId(idInput);
  }
  const onNameChange = (nameInput: string): void => {
    setName(nameInput);
  }
  const handleSubmit = (): void => {
    if (props.isLoading) return;
    props.onSubmit({
      id: getFullIdentifier(id),
      name: name.trim()
    });
  }
  const isValidIdentifier = (id: string) => {
    const asciiRegex = /^[\x00-\x7F\-]+$/;
    if (/\s/.test(id)) {
      return {
        valid: false,
        message: t('clients.identifierMissingWhitespace')
      }
    }
    if(!asciiRegex.test(id)){
      return {
        valid: false,
        message: t('clients.identifierContainNonASCIIChar')
      }
    }
    return {
      valid: true,
      message: "Valid identifier"
    }
  }

  const shouldEnableCreateButton = () => {
    return name && id && isValidIdentifier(id)
  }

  const generateIssuesMessage = (): string => {
    if (!id) return t('clients.identifierRequired')
    const identifierValidationResult = isValidIdentifier(id);
    if(!identifierValidationResult.valid) return identifierValidationResult.message
    if (!name) return t('clients.nameRequired')
    return '';
  };
  const handleClose = (): void => {
    props.onClose()
    resetDialog()
  }
  const resetDialog = (): void => {
    setId('')
    setName('')
  }
  useEffect(() => {
    if(!props.isDialogOpened){
      resetDialog()
    }
  }, [props.isDialogOpened]);

  const getFullIdentifier = (id: string): string => `${props.idPrefix}-${id.trim()}`;

  return (
    <Dialog
      maxWidth={'sm'}
      fullWidth
      className={`clients-dialog ${props.isDialogOpened ? 'open' : ''}`}
      open={props.isDialogOpened}
      onClose={() => props.onClose()}
      data-test='client-dialog'
    >
      <AppCard title={t('clients.addClient')}>
        <AppConfirmDialog
          title={t('clients.copyConfirmationTitle')}
          message={t('clients.copyConfirmationText')}
          cancelLabel={t('common.cancel')}
          confirmLabel={t('common.yes')}
          isDialogOpen={isConfirmCopyOpen}
          onConfirm={() => {
            setIsConfirmCopyOpen(false)
            handleClose()
          }}
          isLoading={false}
          onCancel={() => setIsConfirmCopyOpen(false)}
        />
        {!props.isClientAdded ? (
          <div>
            <AppInput
              label={t('clients.identifier')}
              name='clientIdentifier'
              value={id ?? ''}
              hint={t('clients.identifierRequired')}
              type='identifier'
              onChange={(e) => onIdChange(e.target.value)}
              data-test='client-dialog-client-identifier'
              InputProps={{
                startAdornment: <InputAdornment
                  position='start'
                  sx={{ pt: '21px !important', pb: '3px !important', pr: '2px !important', m: '0 !important', fontSize: '15px' }}
                >
                  {`${props.idPrefix}-`}
                </InputAdornment>
              }}
            />

            <AppSpacer space='15px' />

            <AppInput
              label={t('clients.name')}
              name='clientName'
              value={name ?? ''}
              type='email'
              hint={t('clients.nameRequired')}
              onChange={(e) => onNameChange(e.target.value)}
              data-test='client-dialog-client-name'
            />
          </div>
        ) : (
          <div>
            <ClientInfoDisplay
              id={getFullIdentifier(id)}
              secret={props.secret}
              name={name}
            />
          </div>
        )}
        <footer className='pt-3 mt-4 border-top d-flex align-items-center justify-content-end'>
          {!props.isClientAdded && (
            <AppButton
              variant='secondary'
              onClick={() => handleClose()}
              disabled={props.isLoading}
              data-test='client-dialog-cancel-button'
            >
              {t('common.cancel')}
            </AppButton>
          )}
          {!props.isClientAdded ? (
            <div className='d-flex'>
              <AppButton
                disabled={props.isLoading || !shouldEnableCreateButton() || props.isAddingClient}
                variant='primary'
                className=' ml-2'
                onClick={() => handleSubmit()}
                data-test='client-dialog-submit-button'
              >
                {(
                  <>{props.isAddingClient ? `${t('common.creating')}...` : `${t('common.create')}`}</>
                )}
              </AppButton>
              {generateIssuesMessage() ? (
                <div className='d-flex align-items-center'>
                  <AppLightTooltip title={generateIssuesMessage()} className='d-flex align-items-center'>
                    <>
                      <AppUseIcon name='InfoIcon' width='16' height='16' className='ml-3' color='#ee1b34' />
                    </>
                  </AppLightTooltip>
                </div>
              ) : null}
            </div>
          ) : (
            <div className='d-flex'>
              <CopyClientInfoButton
                id={getFullIdentifier(id)}
                secret={props.secret}
              />
              <AppSpacer space='5px' isHorizontal={true}/>
              <AppButton
                variant='primary'
                className='m1-2'
                onClick={() => setIsConfirmCopyOpen(true)}
                date-test='client-dialog_close-button'
              >
                {t('common.close')}
              </AppButton>
            </div>
          )}
        </footer>
      </AppCard>
    </Dialog>
  );
};

export default ClientsAddDialog;